import { Alert } from '../types';

export class AlertService {
  private static readonly CACHE_KEY = 'alerts_cache';
  private static readonly CACHE_TIMESTAMP_KEY = 'alerts_cache_timestamp';
  private static readonly CACHE_DURATION = 5 * 60 * 1000;

  private static getAlertsUrl(): string {
    const baseUrl = 'https://disgenet-alerts.s3.amazonaws.com';

    // Detect environment
    if (window.location.hostname.includes('test.medbioinformatics.com')) {
      return `${baseUrl}/alerts-test.json`;
    } else if (process.env.NODE_ENV === 'development') {
      return `${baseUrl}/alerts-development.json`;
    } else {
      return `${baseUrl}/alerts.json`;
    }
  }

  static async fetchAlerts(): Promise<Alert[]> {
    try {
      const cached = this.getCachedAlerts();
      if (cached) {
        return cached;
      }
      const response = await fetch(this.getAlertsUrl(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch alerts: ${response.status}`);
      }

      const alerts: Alert[] = await response.json();

      if (!Array.isArray(alerts)) {
        throw new Error('Invalid alerts format: expected array');
      }

      this.setCachedAlerts(alerts);

      return alerts;
    } catch (error) {
      console.error('Error fetching alerts from S3:', error);

      const expiredCache = this.getCachedAlerts(true);
      if (expiredCache) {
        console.warn('Using expired cache as fallback');
        return expiredCache;
      }

      return [];
    }
  }

  private static getCachedAlerts(ignoreExpiration = false): Alert[] | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      const timestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);

      if (!cached || !timestamp) {
        return null;
      }

      const age = Date.now() - parseInt(timestamp, 10);

      if (!ignoreExpiration && age > this.CACHE_DURATION) {
        return null;
      }

      return JSON.parse(cached);
    } catch (error) {
      console.error('Error reading cached alerts:', error);
      return null;
    }
  }

  private static setCachedAlerts(alerts: Alert[]): void {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(alerts));
      localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Error caching alerts:', error);
    }
  }

  static clearCache(): void {
    localStorage.removeItem(this.CACHE_KEY);
    localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
  }

  static async refreshAlerts(): Promise<Alert[]> {
    this.clearCache();
    return this.fetchAlerts();
  }
}
